/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, Edit3, Loader2, Save, Package, X } from "lucide-react";
import { toast } from "sonner";
import { catalogApi, formatApiError } from "@/lib/api";

const CATEGORIES = [
  ["mariage", "Mariage"], ["anniversaire", "Anniversaire"], ["bapteme", "Baptême / Communion"],
  ["entreprise", "Entreprise / Séminaire"], ["bar-restaurant", "Bars & Restaurants"], ["mairie", "Mairie / Collectivité"],
  ["association", "Association"], ["senior", "Senior"], ["photobooth", "Photobooth"],
  ["blindtest", "Blind Test / Karaoké"], ["chanteur", "Chanteur Live"],
  ["billetterie", "Billetterie / Festival"], ["option", "Option / Extra"], ["autre", "Autre"],
];

// Categories that can receive linked options
const LINKABLE_CATEGORIES = CATEGORIES.filter(([k]) => k !== "option" && k !== "billetterie" && k !== "autre");

const EMPTY = { category: "mariage", name: "", subtitle: "", description: "", price_ttc: 0, price_label: "", features: [], duration_hours: null, is_active: true, is_featured: false, admin_only: false, linked_categories: [], sort_order: 0, prestataire_cost: null, urssaf_rate: null, target_net_margin: null, unit: "", min_quantity: null };

function fmtEur(v) { return Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2 }) + " €"; }

/**
 * Calculateur de marge nette pour prestations re-facturées (micro-BNC).
 *
 * Formule inversée : prix_client = (coût_prestataire + marge_nette_visée) / (1 − taux_urssaf)
 * → garantit que la marge NETTE restant après URSSAF est bien celle voulue.
 */
function MarginCalculator({ editing, setEditing }) {
  const cost = Number(editing.prestataire_cost || 0);
  const urssaf = Math.max(0, Math.min(0.5, Number(editing.urssaf_rate ?? 0.22)));
  const target = Number(editing.target_net_margin || 0);
  const denom = 1 - urssaf;
  const computed = denom > 0 && (cost > 0 || target > 0) ? (cost + target) / denom : 0;
  const rounded = Math.ceil(computed / 10) * 10; // arrondi au 10 € supérieur
  const urssafDue = rounded * urssaf;
  const netAfterUrssaf = rounded - urssafDue;
  const realMargin = netAfterUrssaf - cost;
  const canApply = rounded > 0 && rounded !== Number(editing.price_ttc);

  return (
    <div className="mt-2 p-4 border border-purple-400/30 bg-purple-500/[0.05] rounded-sm" data-testid="admin-catalog-margin-calc">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.18em] text-purple-300">Calculateur de marge nette (micro-BNC)</p>
        <span className="text-[10px] text-white/40">URSSAF déduite du CA brut</span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-white/55">Coût prestataire (€)</span>
          <input
            className="input-luxe mt-1 !text-sm"
            type="number" step="1" min="0"
            value={editing.prestataire_cost ?? ""}
            onChange={(e) => setEditing({ ...editing, prestataire_cost: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="500"
            data-testid="admin-catalog-prestataire-cost"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-white/55">Taux URSSAF (%)</span>
          <input
            className="input-luxe mt-1 !text-sm"
            type="number" step="0.1" min="0" max="50"
            value={editing.urssaf_rate != null ? (editing.urssaf_rate * 100).toFixed(1) : ""}
            onChange={(e) => setEditing({ ...editing, urssaf_rate: e.target.value === "" ? null : Number(e.target.value) / 100 })}
            placeholder="22.0"
            data-testid="admin-catalog-urssaf-rate"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-[0.14em] text-white/55">Marge nette voulue (€)</span>
          <input
            className="input-luxe mt-1 !text-sm"
            type="number" step="10" min="0"
            value={editing.target_net_margin ?? ""}
            onChange={(e) => setEditing({ ...editing, target_net_margin: e.target.value === "" ? null : Number(e.target.value) })}
            placeholder="100"
            data-testid="admin-catalog-target-margin"
          />
        </label>
      </div>

      {computed > 0 && (
        <>
          <div className="mt-4 p-3 bg-black/40 border border-purple-400/20">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
              <span className="text-white/60">Prix client calculé :</span>
              <span className="text-right font-mono">{fmtEur(computed)}</span>
              <span className="text-white/60">Prix client arrondi (10 € sup.) :</span>
              <span className="text-right font-mono text-purple-300 font-semibold" data-testid="admin-catalog-rounded-price">{fmtEur(rounded)}</span>
              <div className="col-span-2 border-t border-purple-400/15 my-1.5" />
              <span className="text-white/50">URSSAF à payer ({(urssaf * 100).toFixed(1)}%) :</span>
              <span className="text-right font-mono text-red-300">− {fmtEur(urssafDue)}</span>
              <span className="text-white/50">Net après URSSAF :</span>
              <span className="text-right font-mono">{fmtEur(netAfterUrssaf)}</span>
              <span className="text-white/50">− Coût prestataire :</span>
              <span className="text-right font-mono text-red-300">− {fmtEur(cost)}</span>
              <div className="col-span-2 border-t border-purple-400/15 my-1.5" />
              <span className="text-emerald-300 font-semibold">Marge nette réelle :</span>
              <span className="text-right font-mono text-emerald-300 font-semibold" data-testid="admin-catalog-real-margin">{fmtEur(realMargin)}</span>
            </div>
          </div>

          {canApply && (
            <button
              type="button"
              onClick={() => setEditing({ ...editing, price_ttc: rounded })}
              data-testid="admin-catalog-apply-price"
              className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-[11px] uppercase tracking-[0.18em] text-purple-200 hover:text-white border border-purple-400/50 hover:border-purple-400 hover:bg-purple-500/10 transition"
            >
              Appliquer {fmtEur(rounded)} au prix TTC affiché
            </button>
          )}
        </>
      )}
      <p className="text-[10px] text-white/40 mt-3 leading-relaxed">
        Micro-BNC prestation de services : le CA brut re-facturé au client est <b className="text-white/60">intégralement</b> soumis aux cotisations URSSAF (~22 % en 2026). Ce calculateur inverse la formule pour garantir votre marge nette réelle après cotisations.
      </p>
    </div>
  );
}

export default function AdminCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | EMPTY | item

  const load = () => {
    setLoading(true);
    catalogApi.list()
      .then(setItems)
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => filter === "all" ? items : items.filter((p) => p.category === filter), [items, filter]);
  const grouped = useMemo(() => {
    const g = {};
    for (const p of filtered) (g[p.category] ||= []).push(p);
    return g;
  }, [filtered]);

  const save = async () => {
    try {
      if (editing.id) await catalogApi.update(editing.id, editing);
      else await catalogApi.create(editing);
      toast.success("Forfait enregistré.");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const del = async (p) => {
    if (!window.confirm(`Supprimer "${p.name}" ?`)) return;
    try { await catalogApi.del(p.id); toast.success("Supprimé."); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-catalog">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">Bibliothèque</p>
          <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">Catalogue <span className="italic gold-text">produits.</span></h1>
          <p className="text-white/60 text-sm mt-3 max-w-2xl">
            Vos forfaits, options et tarifs prêts à insérer en un clic dans un devis.
          </p>
        </div>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="admin-catalog-add" className="btn-gold inline-flex items-center gap-2">
          <Plus size={14} strokeWidth={1.5} /> Nouveau forfait
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {[["all", "Tous"], ...CATEGORIES].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`admin-catalog-filter-${k}`}
            className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${filter === k ? "border-[#DCB457] text-[#DCB457] bg-[#DCB457]/8" : "border-white/12 text-white/55 hover:border-[#DCB457]/40"}`}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <p className="text-white/55 italic">Chargement…</p>
        : Object.keys(grouped).length === 0 ? (
          <div className="card-luxe p-12 text-center">
            <Package size={36} strokeWidth={1} className="text-[#DCB457] mx-auto mb-4" />
            <p className="text-white/65 mb-3">Aucun forfait pour le moment.</p>
            <button onClick={() => setEditing({ ...EMPTY })} className="btn-ghost-gold">Créer le premier</button>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([cat, list]) => (
              <section key={cat}>
                <h2 className="font-serif-luxe text-2xl mb-4">{CATEGORIES.find(([k]) => k === cat)?.[1] || cat}</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {list.map((p) => (
                    <article key={p.id} className={`card-luxe p-5 group ${p.is_featured ? "border-[#DCB457]/60" : ""}`} data-testid={`pkg-${p.id}`}>
                      {p.is_featured && (
                        <span className="inline-block bg-[#DCB457] text-black text-[9px] uppercase tracking-[0.22em] font-medium px-2 py-0.5 mb-2">
                          Mis en avant
                        </span>
                      )}
                      {p.admin_only && (
                        <span className="inline-block bg-purple-500/80 text-white text-[9px] uppercase tracking-[0.22em] font-medium px-2 py-0.5 mb-2 ml-1">
                          Admin uniquement
                        </span>
                      )}
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="font-serif-luxe text-lg">{p.name}</h3>
                        <span className="font-serif-luxe text-xl gold-text whitespace-nowrap">{p.price_label || fmtEur(p.price_ttc)}</span>
                      </div>
                      {p.unit === "heure" && <p className="text-[10px] uppercase tracking-[0.18em] text-white/55 mt-1" data-testid={`pkg-hourly-${p.id}`}>Tarif horaire · minimum {p.min_quantity || 1} h</p>}
                      {p.subtitle && <p className="text-[11px] text-[#DCB457]/85 italic mt-1">{p.subtitle}</p>}
                      {p.description && <p className="text-xs text-white/55 mt-2 leading-relaxed">{p.description}</p>}
                      {p.features?.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {p.features.map((f, i) => (
                            <li key={i} className="text-xs text-white/65 before:content-['✦'] before:text-[#DCB457] before:mr-2">{f}</li>
                          ))}
                        </ul>
                      )}
                      {p.linked_categories?.length > 0 && (
                        <p className="text-[10px] uppercase tracking-[0.18em] text-white/45 mt-3">
                          Rattaché à : {p.linked_categories.map((c) => CATEGORIES.find(([k]) => k === c)?.[1] || c).join(", ")}
                        </p>
                      )}
                      <div className="mt-4 pt-3 border-t border-[#DCB457]/10 flex items-center justify-between">
                        <span className={`text-[10px] uppercase tracking-[0.18em] ${p.is_active ? "text-emerald-300" : "text-white/40"}`}>
                          {p.is_active ? "Actif" : "Inactif"}
                        </span>
                        <div className="inline-flex gap-1">
                          <button onClick={() => setEditing(p)} className="p-1.5 border border-white/15 text-white/60 hover:bg-white/5"><Edit3 size={11} strokeWidth={1.5} /></button>
                          <button onClick={() => del(p)} className="p-1.5 border border-red-400/30 text-red-300 hover:bg-red-500/10"><Trash2 size={11} strokeWidth={1.5} /></button>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={(e) => e.target === e.currentTarget && setEditing(null)}>
          <div className="card-luxe p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif-luxe text-2xl">{editing.id ? "Éditer" : "Nouveau"} forfait</h2>
              <button onClick={() => setEditing(null)} className="text-white/55 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className="input-luxe" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
                  {CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input className="input-luxe" type="number" step="0.01" placeholder="Prix TTC" value={editing.price_ttc} onChange={(e) => setEditing({ ...editing, price_ttc: Number(e.target.value) || 0 })} data-testid="admin-catalog-price" />
              </div>
              <input className="input-luxe" placeholder="Nom du forfait *" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="admin-catalog-name" />
              <input className="input-luxe" placeholder="Sous-titre (accroche courte affichée sous le nom sur la page publique)" value={editing.subtitle ?? ""} onChange={(e) => setEditing({ ...editing, subtitle: e.target.value })} data-testid="admin-catalog-subtitle" />
              <input className="input-luxe" placeholder="Libellé de prix (optionnel — ex. 'sur devis', '+45 €'). Vide = affiche le prix TTC formaté." value={editing.price_label ?? ""} onChange={(e) => setEditing({ ...editing, price_label: e.target.value })} data-testid="admin-catalog-price-label" />
              <textarea className="input-luxe min-h-[80px]" placeholder="Description courte" value={editing.description ?? ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              <div>
                <label className="text-[11px] uppercase tracking-[0.18em] text-white/55">Inclus (une fonctionnalité par ligne)</label>
                <textarea className="input-luxe min-h-[80px] mt-1" placeholder="Sonorisation pro&#10;Jeux de lumière&#10;Photobooth inclus" value={(editing.features || []).join("\n")} onChange={(e) => setEditing({ ...editing, features: e.target.value.split("\n") })} />
              </div>
              {editing.category === "option" && (
                <div>
                  <label className="text-[11px] uppercase tracking-[0.18em] text-white/55">Rattacher cette option aux pages publiques</label>
                  <div className="mt-2 flex flex-wrap gap-2" data-testid="admin-catalog-linked">
                    {LINKABLE_CATEGORIES.map(([k, l]) => {
                      const checked = (editing.linked_categories || []).includes(k);
                      return (
                        <button
                          type="button"
                          key={k}
                          data-testid={`admin-catalog-link-${k}`}
                          onClick={() => {
                            const cur = editing.linked_categories || [];
                            const next = checked ? cur.filter((x) => x !== k) : [...cur, k];
                            setEditing({ ...editing, linked_categories: next });
                          }}
                          className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 border transition ${checked ? "border-[#DCB457] bg-[#DCB457]/10 text-[#DCB457]" : "border-white/12 text-white/55 hover:border-[#DCB457]/40"}`}
                        >
                          {l}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-[10px] text-white/40 mt-2">
                    Astuce : cochez les pages Services sur lesquelles cette option doit apparaître dans la section « Options possibles ».
                  </p>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <select className="input-luxe" value={editing.unit || ""} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} data-testid="admin-catalog-unit">
                  <option value="">Unité : forfait</option>
                  <option value="heure">Unité : heure (prix horaire)</option>
                </select>
                {editing.unit === "heure" && (
                  <input className="input-luxe" type="number" step="1" min="1" placeholder="Minimum d'heures" value={editing.min_quantity ?? ""} onChange={(e) => setEditing({ ...editing, min_quantity: e.target.value === "" ? null : Number(e.target.value) })} data-testid="admin-catalog-min-qty" />
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input className="input-luxe" type="number" step="0.5" placeholder="Durée (h)" value={editing.duration_hours ?? ""} onChange={(e) => setEditing({ ...editing, duration_hours: e.target.value === "" ? null : Number(e.target.value) })} />
                <input className="input-luxe" type="number" step="1" placeholder="Ordre d'affichage" value={editing.sort_order ?? 0} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} data-testid="admin-catalog-sort" />
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} className="accent-[#DCB457]" />
                  <span className="text-white/75">Actif</span>
                </label>
              </div>
              <label className="flex items-center gap-2 text-sm border border-[#DCB457]/25 px-3 py-2 cursor-pointer hover:bg-[#DCB457]/5">
                <input type="checkbox" checked={!!editing.is_featured} onChange={(e) => setEditing({ ...editing, is_featured: e.target.checked })} className="accent-[#DCB457]" data-testid="admin-catalog-featured" />
                <span className="text-white/85">Mettre en avant (« Le plus choisi » sur la page publique)</span>
              </label>
              <label className="flex items-center gap-2 text-sm border border-purple-400/30 px-3 py-2 cursor-pointer hover:bg-purple-500/5">
                <input type="checkbox" checked={!!editing.admin_only} onChange={(e) => setEditing({ ...editing, admin_only: e.target.checked })} className="accent-purple-500" data-testid="admin-catalog-admin-only" />
                <span className="text-white/85">Admin uniquement (non visible sur le site public — utilisable en devis)</span>
              </label>

              {editing.admin_only && (
                <MarginCalculator editing={editing} setEditing={setEditing} />
              )}
              <div className="pt-3 flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="btn-ghost-gold">Annuler</button>
                <button onClick={save} data-testid="admin-catalog-save" className="btn-gold inline-flex items-center gap-2">
                  <Save size={14} /> Enregistrer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
